import { useMemo, useState } from 'react'
import { Camera, Trash } from '@phosphor-icons/react'
import { useFormat } from '@/i18n'
import { Banner, Button, Field, KgInput, Modal, Select, Spinner, TextArea, useToast } from '@/ui'
import { errorMessage } from '../api/core'
import { useCorrectLot, type LotDetail } from '../api/gate'
import { uploadEvidence } from '../api/reference'
import { gramsToKgString, inrToPaise0 } from '../api/mappers'
import { useRateCard, useSackTypes } from '../api/setup'
import { PAYOUT_ACTION_TEXT, previewCorrection, type PayoutAction } from '../model/lotMath'
import { MoneyEffectPreview } from './MoneyEffectPreview'
import { PhotoViewer } from './PhotoViewer'

export interface CorrectionDialogProps {
  open: boolean
  onClose: () => void
  lot: LotDetail
  /** Settle this OPEN DISPUTE as SUPERVISOR_CORRECTED in the same save (F4-B2.d). */
  disputeExceptionId?: string
  defaultContainerId?: string
  title?: string
}

/**
 * Correct a bag of a committed lot (F4.10-h): pick the bag, a new gross weight and/or sack type,
 * a reason and an evidence photo. The money effect is shown before saving, computed exactly as
 * the server will; after saving the server's own figure is confirmed.
 */
export function CorrectionDialog({ open, onClose, lot, disputeExceptionId, defaultContainerId, title }: CorrectionDialogProps) {
  const fmt = useFormat()
  const toast = useToast()
  const live = lot.bags.filter((b) => b.status !== 'VOID')
  const [containerId, setContainerId] = useState(defaultContainerId ?? live[0]?.id ?? '')
  const [grossG, setGrossG] = useState<number | null>(null)
  const [sackTypeId, setSackTypeId] = useState('')
  const [reason, setReason] = useState('')
  const [evidence, setEvidence] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [touched, setTouched] = useState(false)
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setContainerId(defaultContainerId ?? live[0]?.id ?? '')
      setGrossG(null)
      setSackTypeId('')
      setReason('')
      setEvidence(null)
      setTouched(false)
    }
  }

  const card = useRateCard(open ? lot.rateCardId : null)
  const sacks = useSackTypes()
  const save = useCorrectLot(lot.id)
  const bag = lot.bags.find((b) => b.id === containerId) ?? null
  const sack = sacks.data?.rows.find((s) => s.id === sackTypeId) ?? null
  const rules = card.data?.rows[0]?.deductionRules ?? null

  const preview = useMemo(
    () =>
      previewCorrection({
        bags: lot.bags.map((b) => ({ id: b.id, status: b.status, grossG: b.grossG, tareG: b.tareG })),
        containerId,
        newGrossG: grossG,
        newTareG: sack ? sack.tareG : null,
        ratePaisePerKg: lot.ratePaisePerKg ?? 0,
        rules,
        moisturePct: lot.moisturePct,
        currentAmountPaise: lot.amountPaise ?? 0,
        ledgerBalancePaise: lot.ledgerBalancePaise,
        payouts: lot.payouts,
      }),
    [lot, containerId, grossG, sack, rules],
  )

  const reasonError = touched && !reason.trim() ? 'A reason is required' : null
  const rulesLoading = Boolean(lot.rateCardId) && card.isLoading

  const onPhoto = async (file: File | undefined) => {
    if (!file) return
    setUploading(true)
    try {
      setEvidence(await uploadEvidence(file))
    } catch (e) {
      toast.error('Photo not uploaded', errorMessage(e))
    } finally {
      setUploading(false)
    }
  }

  const submit = async () => {
    setTouched(true)
    if (!reason.trim() || !preview.ok) return
    try {
      const r = await save.mutateAsync({
        containerId,
        grossKg: grossG !== null && grossG !== bag?.grossG ? gramsToKgString(grossG) : undefined,
        sackTypeId: sackTypeId && sackTypeId !== bag?.sackTypeId ? sackTypeId : undefined,
        reason: reason.trim(),
        evidenceMediaId: evidence ?? undefined,
        disputeExceptionId,
      })
      const server = inrToPaise0(r.adjustmentInr)
      if (server !== preview.adjustmentPaise)
        toast.show({
          tone: 'warn',
          title: 'Saved — the server figure differs from the preview',
          description: `Server: ${fmt.money(server)}, preview: ${fmt.money(preview.adjustmentPaise)}.`,
        })
      else if (r.payoutAction && r.payoutAction !== 'NONE')
        toast.show({ tone: 'info', title: 'Payment', description: PAYOUT_ACTION_TEXT[r.payoutAction as PayoutAction] ?? r.payoutAction })
      onClose()
    } catch {
      /* toasted by the mutation */
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={!save.isPending}
      title={title ?? `Correct a bag of ${lot.code}`}
      description="A supervisor's correction makes a new weight that supersedes the gate weight; the old one is kept."
      footer={
        <>
          <Button variant="ghost" className="ml-auto" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={save.isPending} disabled={!preview.ok || uploading || rulesLoading}>
            Save correction
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        {disputeExceptionId && (
          <Banner tone="info">Saving also settles the open dispute as “supervisor corrected”.</Banner>
        )}
        <Field label="Bag" required>
          <Select
            value={containerId}
            onChange={(v) => {
              setContainerId(v)
              setGrossG(null)
              setSackTypeId('')
            }}
            options={live.map((b) => ({
              value: b.id,
              label: `${b.tag ?? b.conflictTag ?? b.id.slice(0, 8)} · ${fmt.weight(b.grossG)} gross · ${b.sackTypeCode ?? 'sack ?'}`,
            }))}
          />
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="New gross weight" hint={bag ? `Now ${fmt.weight(bag.grossG)}` : undefined}>
            <KgInput size="md" value={grossG} onChange={setGrossG} placeholder={bag ? (bag.grossG / 1000).toFixed(3) : '0.000'} />
          </Field>
          <Field label="Sack type" hint={bag?.sackTypeCode ? `Now ${bag.sackTypeCode}` : undefined}>
            <Select
              value={sackTypeId}
              onChange={setSackTypeId}
              placeholder="Keep the same"
              options={(sacks.data?.rows ?? [])
                .filter((s) => s.active)
                .map((s) => ({ value: s.id, label: `${s.code} · ${s.name} · ${fmt.weight(s.tareG)}` }))}
            />
          </Field>
        </div>
        <Field label="Reason" required error={reasonError}>
          <TextArea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g. re-weighed on the stamped scale in front of the farmer" maxLength={300} />
        </Field>
        <Field label="Evidence photo" hint="Optional — the scale display or the re-check.">
          <div className="flex items-center gap-3">
            {evidence ? (
              <>
                <PhotoViewer photo={evidence} alt="Evidence" size="sm" />
                <Button size="sm" variant="ghost" leadingIcon={<Trash />} onClick={() => setEvidence(null)}>
                  Remove
                </Button>
              </>
            ) : (
              <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-chip border border-ink/80 px-3 font-mono text-[11px] font-bold uppercase tracking-label hover:bg-ink hover:text-paper">
                {uploading ? <Spinner /> : <Camera aria-hidden="true" />}
                {uploading ? 'Uploading…' : 'Add photo'}
                <input type="file" accept="image/*" className="sr-only" onChange={(e) => void onPhoto(e.target.files?.[0])} />
              </label>
            )}
          </div>
        </Field>
        <div className="rounded-card border border-rule bg-surface p-3">
          <div className="eyebrow mb-2">Money effect · before saving</div>
          {rulesLoading ? <Spinner label="Loading the rate card" /> : <MoneyEffectPreview preview={preview} />}
        </div>
      </form>
    </Modal>
  )
}
